import * as React from 'react';
import {Glyphs, MenuItem} from '@techsmith/tsc-cloud-style-guide';
import conversationApi from '../../service/conversationApi';
import UserMessaging from '../../util/UserMessaging';
import config from '../../service/config';
import {useTranslation} from 'react-i18next';
import {SetupWorkerState} from '../../model/workerState';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import logging from '../../service/logging';
import {useCallback} from 'react';

export const NotificationToggleBase: React.FC<IStateMappedProps & INotificationToggleProps> = ({userAuthoredComment, VideoReviewId}) => {
   const {t} = useTranslation();
   const [isNotificationEnabled, setIsNotificationEnabled] = React.useState(false);
   const [loadingState, setLoadingState] = SetupWorkerState();

   const fetchNotificationPreference = useCallback(async () => {
      try {
         setLoadingState({isWorking: true, hasError: false});
         const isEnabled = await conversationApi.isSubscribedToConversation(VideoReviewId);
         if (isEnabled === null) {
            setIsNotificationEnabled(config.watchConfig.isOwner || userAuthoredComment);
         } else {
            setIsNotificationEnabled(isEnabled);
         }
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to fetch notification preference', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   }, [VideoReviewId, setLoadingState, userAuthoredComment]);

   React.useEffect(() => {
      void fetchNotificationPreference();
   }, [VideoReviewId, fetchNotificationPreference, userAuthoredComment]);

   const toggleNotificationPreference = async (): Promise<void> => {
      if (!loadingState.isWorking) {
         try {
            setLoadingState({isWorking: true, hasError: false});
            const newNotificationValue = !isNotificationEnabled;
            setIsNotificationEnabled(newNotificationValue);
            if (newNotificationValue) {
               await conversationApi.subscribeToConversation(VideoReviewId);
            } else {
               await conversationApi.unsubscribeFromConversation(VideoReviewId);
            }
            setLoadingState({isWorking: false, hasError: false});
         } catch (e) {
            setLoadingState({isWorking: false, hasError: true});
            logging.error('Failed to toggle notification preference', e);
            UserMessaging.warn(t('conversations.failedUpdatePreference'));
         }
      }
   };

   return (
      <MenuItem
         glyph={isNotificationEnabled ? <Glyphs.Alert16x16 /> : <Glyphs.AlertOutline16x16 />}
         label={isNotificationEnabled ? t('conversations.actions.unsubscribeToConversationAlerts') : t('conversations.actions.subscribeToConversationAlerts')}
         onClick={toggleNotificationPreference}
         isDisabled={loadingState.isWorking}
         testId="conversation-notification-toggle"
      />
   );
};

export interface INotificationToggleProps {
   userAuthoredComment: boolean;
}

export interface IStateMappedProps {
   VideoReviewId: string;
}

const mapStatesToProps = (
   {userAuthoredComment}: Partial<INotificationToggleProps>,
   {VideoReviewId}: Partial<IStateMappedProps>
): IStateMappedProps & INotificationToggleProps => ({userAuthoredComment, VideoReviewId});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore)(NotificationToggleBase);
